import { useEffect, useState } from "react";
import api from "../../lib/api";
import { apiError } from "../../lib/finance";
import { Skeleton } from "../ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Download, ExternalLink } from "lucide-react";

// Where a receipt's bytes come from: a file chosen in this browser (not sent yet, or staged), a piece waiting in
// « À saisir », or a receipt already attached to an expense. The API never serves receipts inline, so server files are
// fetched as a blob; every object URL is released when the source changes.
export const receiptSource = {
  local: (file) => ({ key: `local:${file.name}:${file.size}:${file.lastModified}`, file }),
  piece: (id) => ({ key: `piece:${id}`, path: `/finance/inbox/${id}/file` }),
  attachment: (id) => ({ key: `attachment:${id}`, path: `/finance/attachments/${id}` }),
};

const LOADING = { status: "loading", url: null, mime: null, error: "" };

function useReceiptBlob(source) {
  const [state, setState] = useState(LOADING);
  const key = source?.key;

  useEffect(() => {
    if (!source) return undefined;
    let url = null;
    let cancelled = false;
    const show = (blob) => {
      if (cancelled) return;
      url = URL.createObjectURL(blob);
      setState({ key, status: "ready", url, mime: blob.type, error: "" });
    };
    setState({ key, status: "loading", url: null, mime: null, error: "" });
    if (source.file) {
      show(source.file);
    } else {
      api.get(source.path, { responseType: "blob" })
        .then(({ data }) => show(data))
        .catch(async (err) => {
          // A blob response hides the JSON error body: read it back to show the server's message.
          let message = "Aperçu indisponible.";
          try { message = apiError({ response: { data: JSON.parse(await err.response.data.text()) } }); } catch { /* keep the generic message */ }
          if (!cancelled) setState({ key, status: "error", url: null, mime: null, error: message });
        });
    }
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Until the effect runs for a new source, never hand back the previous one's (already revoked) URL.
  return state.key === key ? state : LOADING;
}

function ReceiptView({ state, name, tall }) {
  const height = tall ? "h-[70vh]" : "h-72 lg:h-[60vh]";
  if (state.status === "loading") return <Skeleton className={`${height} w-full`} />;
  if (state.status === "error") return <p className="text-sm text-slate-600 py-6 text-center" role="alert">{state.error}</p>;
  return state.mime === "application/pdf" ? (
    <iframe src={state.url} title={`Aperçu de ${name}`} className={`w-full ${height} rounded-md border border-slate-200 bg-white`} />
  ) : (
    <img src={state.url} alt={`Justificatif ${name}`} className={`w-full ${tall ? "max-h-[70vh]" : "max-h-72 lg:max-h-[60vh]"} object-contain rounded-md border border-slate-200 bg-slate-50`} />
  );
}

function OpenLink({ url }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-jamora hover:underline shrink-0">
      Ouvrir en grand <ExternalLink size={12} />
    </a>
  );
}

// Inline preview, next to the form in serial-entry mode.
export default function ReceiptPreview({ source, name }) {
  const state = useReceiptBlob(source);
  return (
    <figure className="space-y-2" data-testid="finance-piece-preview">
      <ReceiptView state={state} name={name} />
      {state.status === "ready" && (
        <figcaption className="flex items-center justify-between gap-2 text-xs text-slate-500">
          <span className="truncate">{name}</span>
          <OpenLink url={state.url} />
        </figcaption>
      )}
    </figure>
  );
}

// The same preview in a dialog, opened by clicking a receipt's name. `receipt` = {source, name} or null (closed).
export function ReceiptPreviewDialog({ receipt, onClose, description = "Aperçu du justificatif." }) {
  const state = useReceiptBlob(receipt?.source);
  const name = receipt?.name || "justificatif";
  return (
    <Dialog open={!!receipt} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl w-[calc(100vw-2rem)]" data-testid="finance-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">{name}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {receipt && <ReceiptView state={state} name={name} tall />}
        {state.status === "ready" && (
          <div className="flex items-center justify-end gap-4 text-sm">
            <OpenLink url={state.url} />
            <a href={state.url} download={name} className="inline-flex items-center gap-1 text-jamora hover:underline" data-testid="finance-receipt-download">
              Télécharger <Download size={14} />
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
